import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, REQUEST, REQUEST_CONTEXT, signal, TransferState } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { RestService } from 'app/services/rest/rest.service';
import { LandingPageConfig } from './landing-page.models';
import { SupportedLanguage } from 'app/services/language/languages';

const LANDING_STATE_KEY = makeStateKey<LandingPageConfig>('landing-page');

@Injectable({ providedIn: 'root' })
export class LandingPageDataService {
  readonly currentConfig = signal<LandingPageConfig | null>(null);

  constructor(
    private restService: RestService,
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null,
    @Optional() @Inject(REQUEST_CONTEXT) private requestContext: { landingPage?: LandingPageConfig } | null,
  ) {}

  get initialConfig(): LandingPageConfig | null {
    if (this.transferState.hasKey(LANDING_STATE_KEY)) {
      return this.transferState.get(LANDING_STATE_KEY, null);
    }
    return this.requestContext?.landingPage ?? null;
  }

  load(language: SupportedLanguage): Observable<LandingPageConfig> {
    if (this.transferState.hasKey(LANDING_STATE_KEY)) {
      const data = this.transferState.get(LANDING_STATE_KEY, null);
      this.transferState.remove(LANDING_STATE_KEY);
      return of(data).pipe(tap(config => this.currentConfig.set(config)));
    }

    if (isPlatformServer(this.platformId)) {
      if (this.requestContext?.landingPage) {
        const data = this.requestContext.landingPage;
        this.transferState.set(LANDING_STATE_KEY, data);
        return of(data).pipe(tap(config => this.currentConfig.set(config)));
      }

      if (!this.request) {
        throw new Error('SSR necesita una URL de petición para cargar la landing.');
      }

      const localizedUrl = new URL(`${environment.urlServer}/public/landingPage`, this.request.url);
      localizedUrl.searchParams.set('language', language.toUpperCase());
      return this.http.get<LandingPageConfig>(localizedUrl.toString()).pipe(
        tap(data => {
          this.currentConfig.set(data);
          this.transferState.set(LANDING_STATE_KEY, data);
        }),
      );
    }

    return this.restService.getLandingPage(language).pipe(
      tap(data => this.currentConfig.set(data)),
    );
  }
}
